import { StudyState } from "@app/types";
import { Id } from "../types/id.type";
import { Attribute } from "./attributes.model";
import { FileMeta } from "./file-meta.model";
import { Search } from "./search.model";
import { PeerReviewStatus } from "../types/peerReview.type";
import { ReviewItemType } from "../types/reviewItemType.type";
import { INCLUDED } from "../types/included-screening-criterion.type";

interface WithState {
  get state(): StudyState;
}

export abstract class ReviewItem implements WithState {
  id: Id;
  type: ReviewItemType;
  parentStudyId?: Id;
  maybeParentStudyId?: number;
  isFavorite?: boolean;
  complaintDate?: Date;
  titleAndAbstractScreening?: string;
  fullTextScreening: string;
  suggestedFullTextScreening?: string;
  search: Search;
  pdfFile?: FileMeta;
  reference?: string;
  appraisal?: { [key: string]: string };
  oxfordLevelOfEvidence?: string;
  synthesis: { attributes: Attribute[] };
  peerReviewStatus?: PeerReviewStatus;
  metadata?: any;
  cachedPdfPrice?: number | null;

  constructor(data: {
    id?: Id;
    isFavorite?: boolean;
    complaintDate?: Date;
    search: Search;
    titleAndAbstractScreening?: string;
    fullTextScreening?: string;
    suggestedFullTextScreening?: string;
    parentStudyId?: Id;
    maybeParentStudyId?: number;
    pdfFile?: FileMeta;
    appraisal?: { [key: string]: string };
    oxfordLevelOfEvidence?: string;
    synthesis?: { attributes: Attribute[] };
    peerReviewStatus?: PeerReviewStatus;
  }) {
    this.id = data.id ?? 0;
    this.isFavorite = data.isFavorite ?? false;
    this.complaintDate = data.complaintDate;
    this.search = data.search;
    this.titleAndAbstractScreening = data.titleAndAbstractScreening ?? "";
    this.fullTextScreening = data.fullTextScreening ?? "";
    this.suggestedFullTextScreening = data.suggestedFullTextScreening ?? "";
    this.parentStudyId = data.parentStudyId;
    this.maybeParentStudyId = data.maybeParentStudyId;
    this.pdfFile = data.pdfFile;
    this.appraisal = data.appraisal ?? {};
    this.oxfordLevelOfEvidence = data.oxfordLevelOfEvidence ?? "";
    this.synthesis = data.synthesis ?? { attributes: [] };
    this.peerReviewStatus = data.peerReviewStatus ?? PeerReviewStatus.default;
    this.type = ReviewItemType.default;
  }

  get state(): StudyState {
    if (this.isDuplicate) return StudyState.DUPLICATE;
    if (this.fullTextScreening === INCLUDED) return StudyState.INCLUDED;
    if (
      (this.titleAndAbstractScreening && this.titleAndAbstractScreening != INCLUDED) ||
      (this.fullTextScreening && this.fullTextScreening !== INCLUDED)
    )
      return StudyState.EXCLUDED;
    if (
      this.type === ReviewItemType.Article &&
      (!this.metadata.abstract ||
        !this.metadata.title ||
        this.metadata.abstract.startsWith("...") ||
        this.metadata.abstract.endsWith("...") ||
        this.metadata.abstract.toLowerCase() === "n/a" ||
        this.maybeDuplicate ||
        !this.metadata?.publishYear ||
        (this.metadata?.authors?.length ?? 0) === 0)
    )
      return StudyState.QUARANTINED;
    return StudyState.UNSCREENED;
  }
  get isDuplicate(): boolean {
    return !!this.parentStudyId;
  }

  get maybeDuplicate(): boolean {
    return !!this.maybeParentStudyId;
  }

  markAsDuplicate(parentStudyId: number) {
    this.titleAndAbstractScreening = "";
    this.fullTextScreening = "";
    this.parentStudyId = parentStudyId;
    this.maybeParentStudyId = undefined;
  }

  markAsNotDuplicate() {
    this.parentStudyId = undefined;
    this.maybeParentStudyId = undefined;
  }

  setTitleAndAbstractScreening(key: string) {
    this.titleAndAbstractScreening = key;
  }

  clearTitleAndAbstractScreening() {
    this.titleAndAbstractScreening = "";
    this.fullTextScreening = "";
  }

  setFullStudyScreening(key: string) {
    if (!this.titleAndAbstractScreening) throw new Error("title and abstract screening missing");
    this.fullTextScreening = key;
  }

  clearFullStudyScreening() {
    this.fullTextScreening = "";
  }
}
